/* eslint-disable react/forbid-component-props */
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import Cookies from 'react-cookies';
import { MAP_WIDGET_BG } from '../../config/constants/images';
import COOKIES from '../../config/cookies/cookies';
import { MAP_VIEW_URL } from '../../config/links/links';
import { DEFAULT_LOCATION } from '../../config/setup/setup';
import { MAP, SHOW_ON_MAP } from '../../config/text/text';
import { trackEvent } from '../../helpers/analytics';
import useCheckMobile from '../../helpers/checkMobile';
import { redirectToURL } from '../../helpers/url';
import ThemeContext from '../../providers/ThemeProvider';
import PrimaryButton from '../_generic/button/PrimaryButton';

const MAP_MARKER_OUTLINED = {
  icon: [
    448,
    448,
    [],
    null,
    'M1094 788c-4 0-8-1-12-3l-332-122h0l-336 118a126 126 0 0 1-39 7c-13 0-27-3-39-7l-297-103a63 63 0 0 1-39-58v-676c0-18 15-32 31-32 4 0 8 1 12 3l332 123 336-119a125 125 0 0 1 78 0l297 103a63 63 0 0 1 39 58v676c0 18-15 32-31 32z m-656-115l250-88v-558l-250 88v558z m-344-639v564l250 87v-559l-1-1-249-91z m937 68l-250-87v559l2 1 248 92v-565z',
  ],
  iconName: 'map-marker-outlined',
  prefix: 'fas',
};
library.add(MAP_MARKER_OUTLINED);

const MapViewWidget = ({ categoryQuery = '' }) => {
  const router = useRouter();
  const theme = useContext(ThemeContext);
  const isMobile = useCheckMobile(false);
  const [isOpenAppButtonVisible, setIsOpenAppButtonVisible] = useState(false);
  const [isWidgetVisible, setIsWidgetVisible] = useState(true);

  const [mapViewUrl, setMapViewUrl] = useState(MAP_VIEW_URL);

  // Logic for showing fixed widget button when
  // the widget hides on scrolling down the page.
  const showWidgetButton = () => {
    const scrollPosition = window.scrollY;
    const widgetElement = document.querySelector('.map-view-widget');

    if (widgetElement) {
      const widgetBottom = widgetElement.offsetTop + widgetElement.offsetHeight;

      // Adjust the value based on when you want the widget to hide
      setIsWidgetVisible(scrollPosition < widgetBottom);
    }
  };

  useEffect(() => {
    const defaultLocation = DEFAULT_LOCATION[process.env.NEXT_PUBLIC_SITE];
    let lat = defaultLocation.latLon.lat;
    let lon = defaultLocation.latLon.lon;

    // if lat lon in url use those
    const locationCookie = Cookies.load(COOKIES.location);
    if (locationCookie && locationCookie.lat && locationCookie.lon) {
      lat = locationCookie.lat;
      lon = locationCookie.lon;
    }

    if (lat && lon && !categoryQuery.includes('bottomRightLat')) {
      setMapViewUrl(
        `${MAP_VIEW_URL}?lat=${lat.toFixed(3)}&lon=${lon.toFixed(3)}${
          categoryQuery ? '&' + categoryQuery : ''
        }`,
      );
    } else {
      setMapViewUrl(
        `${MAP_VIEW_URL}${categoryQuery ? '?' + categoryQuery : ''}`,
      );
    }
  }, [categoryQuery]);

  useEffect(() => {
    const isOpenAppElement = document.querySelector('.app-button-wrapper');
    if (isOpenAppElement) {
      setIsOpenAppButtonVisible(true);
    }
    // For smaller laptop/mobile screens,
    // check if Widget is visible initial load
    showWidgetButton();
    // Attach the event listener when the component mounts
    if (isWidgetVisible) {
      window.addEventListener('scroll', showWidgetButton);
    }

    // Detach the event listener when the component unmounts
    return () => {
      window.removeEventListener('scroll', showWidgetButton);
    };
  }, []);

  const handleWidgetClick = () => {
    if (isMobile) {
      trackEvent('mv_ widget_click_mobile');
    } else {
      trackEvent('mv_widget_click_desktop');
    }
    // save this page path for back button
    const path = router.asPath;
    if (path) Cookies.save(COOKIES.mapBackPath, path, 365);

    redirectToURL(mapViewUrl, router);
  };

  return (
    <>
      <div className="map-view-widget">
        <div className="widget-bg" />
        <PrimaryButton
          className={classNames(
            'primary-button map-view-btn',
            'less-rounded font-weight-normal',
            'capitalize size-medium',
          )}
          onClick={handleWidgetClick}
        >
          <FontAwesomeIcon
            color="white"
            icon="map-marker-outlined"
            style={{ fontSize: '7.5px', marginTop: '-4px' }}
            swapOpacity
          />
          <span>{SHOW_ON_MAP}</span>
        </PrimaryButton>
      </div>
      {(!isWidgetVisible || isMobile) && (
        <div
          className={classNames('map-view-btn-group', {
            'open-app-btn-visible': isOpenAppButtonVisible,
          })}
        >
          <PrimaryButton
            className={classNames(
              'primary-button map-view-btn',
              'capitalize size-large',
            )}
            onClick={handleWidgetClick}
          >
            <FontAwesomeIcon
              color="white"
              icon="map-marker-outlined"
              style={{
                fontSize: '10px',
                margin: '-5px 6px 0 0',
              }}
            />
            <span>{MAP}</span>
          </PrimaryButton>
        </div>
      )}
      <style jsx>{`
        .map-view-btn-group {
          position: fixed;
          bottom: 3rem;
          left: 50%;
          transform: translate(-50%);
          z-index: 1000;
        }
        .map-view-widget {
          display: none;
          height: 104px;
          padding: 2rem;
          margin-bottom: 1rem;
          margin-top: -10px;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
          overflow: hidden;
          background-image: url(${MAP_WIDGET_BG});
          background-size: cover;
        }

        .widget-bg {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          background-size: 200px;
          z-index: -1;
        }

        :global(.map-view-btn) {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 2rem;
          height: 4.375rem;
          margin-top: -1px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4);
        }

        .map-icon {
          width: 3.5rem;
          height: 3.5rem;
        }

        .open-app-btn-visible.map-view-btn-group {
          bottom: 8rem;
        }

        @media (min-width: ${theme.breakpoints.mdUp}) {
          .map-view-btn-group {
            position: fixed;
            bottom: 6.5rem;
          }
        }
        @media (min-width: ${theme.breakpoints.xlUp}) {
          .map-view-widget {
            display: flex;
          }
        }
      `}</style>
    </>
  );
};

MapViewWidget.propTypes = {
  categoryQuery: PropTypes.string,
};

export default MapViewWidget;
